import { useState, useEffect } from 'react';

const API = import.meta.env.VITE_API_URL || '';

function shortName(n) {
  if (!n) return '';
  const v = n.vorname || '';
  const nm = n.name || '';
  return nm ? `${v} ${nm[0]}.` : v;
}

function fmtTime(d) {
  return d.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtDate(d) {
  return d.toLocaleDateString('de-AT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function fmtUhr(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
}

function pauseDauer(iso, jetzt) {
  if (!iso) return '';
  const diff = Math.floor((jetzt - new Date(iso)) / 1000);
  const m = Math.floor(diff / 60);
  const s = diff % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function AbwesenheitDisplayPage() {
  const [data, setData] = useState(null);
  const [zeit, setZeit] = useState(new Date());

  // Clock every second (for pause timers)
  useEffect(() => {
    const t = setInterval(() => setZeit(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Data polling every 30s
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/abwesenheit/tv`);
        if (res.ok) setData(await res.json());
      } catch (e) { /* silent */ }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  // Wake lock
  useEffect(() => {
    let wl;
    (async () => { try { wl = await navigator.wakeLock?.request('screen'); } catch (e) { /* */ } })();
    return () => { wl?.release?.(); };
  }, []);

  if (!data) {
    return (
      <div style={{ background: '#0F172A', color: '#F8FAFC', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 48, height: 48, border: '3px solid #338edc', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const { anwesend, in_pause, gegangen, urlaub, krank, frei, zeitausgleich, flex } = data;

  return (
    <div style={{
      background: '#0F172A', color: '#F8FAFC', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif",
      display: 'flex', flexDirection: 'column', padding: '24px 32px', boxSizing: 'border-box', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'linear-gradient(135deg, #338edc 0%, #6366F1 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 18,
          }}>W</div>
          <span style={{ fontSize: 28, fontWeight: 700 }}>Anwesenheit</span>
          <span style={{ fontSize: 18, color: '#94A3B8', marginLeft: 8 }}>{fmtDate(zeit)}</span>
        </div>
        <div style={{ fontSize: 48, fontWeight: 700, letterSpacing: -1, lineHeight: 1 }}>{fmtTime(zeit)}</div>
      </div>

      {/* Main grid */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0, overflow: 'hidden' }}>

        {/* Anwesend */}
        <Section icon="\u2705" title="Anwesend" count={anwesend.length} color="#22C55E" flex={3}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
            {anwesend.map(p => (
              <div key={p.mitarbeiter_id} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 220 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22C55E', flexShrink: 0 }} />
                <span style={{ fontSize: 20, fontWeight: 500 }}>{shortName(p)}</span>
                <span style={{ fontSize: 16, color: '#64748B' }}>seit {fmtUhr(p.seit)}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Pause */}
        {in_pause.length > 0 && (
          <Section icon="\u2615" title="Pause" count={in_pause.length} color="#F59E0B" flex={1}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
              {in_pause.map(p => (
                <div key={p.mitarbeiter_id} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 320 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#F59E0B', flexShrink: 0 }} />
                  <span style={{ fontSize: 20, fontWeight: 500 }}>{shortName(p)}</span>
                  <span style={{ fontSize: 16, color: '#94A3B8' }}>
                    {p.pause_typ === 'mittagspause' ? 'Mittagspause' : 'Kurze Pause'}
                  </span>
                  <span style={{ fontSize: 16, color: '#F59E0B', fontWeight: 600 }}>
                    seit {fmtUhr(p.pause_seit)} ({pauseDauer(p.pause_seit, zeit)})
                  </span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Bottom row: Urlaub / Krank / ZA / Frei / Flex */}
        <div style={{ display: 'flex', gap: 16, flexShrink: 0 }}>
          {urlaub.length > 0 && (
            <MiniSection icon="\uD83C\uDFD6\uFE0F" title="Urlaub" count={urlaub.length} color="#338edc">
              {urlaub.map((p, i) => <span key={i} style={{ fontSize: 18 }}>{shortName(p)}</span>)}
            </MiniSection>
          )}
          {krank.length > 0 && (
            <MiniSection icon="\uD83E\uDD12" title="Krank" count={krank.length} color="#EF4444">
              {krank.map((p, i) => <span key={i} style={{ fontSize: 18 }}>{shortName(p)}</span>)}
            </MiniSection>
          )}
          {zeitausgleich.length > 0 && (
            <MiniSection icon="\u23F0" title="ZA" count={zeitausgleich.length} color="#8B5CF6">
              {zeitausgleich.map((p, i) => <span key={i} style={{ fontSize: 18 }}>{shortName(p)}</span>)}
            </MiniSection>
          )}
          {frei.length > 0 && (
            <MiniSection icon="\uD83D\uDCCB" title="Frei" count={frei.length} color="#64748B">
              {frei.map((p, i) => <span key={i} style={{ fontSize: 18 }}>{shortName(p)}</span>)}
            </MiniSection>
          )}
          {flex.length > 0 && (
            <MiniSection icon="\uD83D\uDFE0" title="Flex-Worker" count={flex.length} color="#F97316">
              {flex.map((p, i) => <span key={i} style={{ fontSize: 18 }}>{shortName(p)}</span>)}
            </MiniSection>
          )}
          {gegangen.length > 0 && (
            <MiniSection icon="\uD83D\uDEAA" title="Gegangen" count={gegangen.length} color="#475569">
              {gegangen.map((p, i) => (
                <span key={i} style={{ fontSize: 18, color: '#64748B' }}>{shortName(p)}</span>
              ))}
            </MiniSection>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
      `}</style>
    </div>
  );
}

function Section({ icon, title, count, color, children, flex = 1 }) {
  return (
    <div style={{
      background: '#1E293B', borderRadius: 12, border: '1px solid #334155',
      padding: '16px 24px', flex, minHeight: 0, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 20 }}>{icon}</span>
        <span style={{ fontSize: 16, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
          {title}
        </span>
        <span style={{
          fontSize: 14, fontWeight: 700, color: '#0F172A', background: color,
          borderRadius: 12, padding: '2px 10px', minWidth: 28, textAlign: 'center',
        }}>{count}</span>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {children}
      </div>
    </div>
  );
}

function MiniSection({ icon, title, count, color, children }) {
  return (
    <div style={{
      background: '#1E293B', borderRadius: 12, border: '1px solid #334155',
      padding: '14px 20px', flex: 1, minWidth: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span style={{ fontSize: 14, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>
          {title}
        </span>
        <span style={{
          fontSize: 13, fontWeight: 700, color: '#0F172A', background: color,
          borderRadius: 10, padding: '1px 8px',
        }}>{count}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
        {children}
      </div>
    </div>
  );
}
